import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const reviews = [
  {
    id: 1,
    username: "Poddster",
    text: "Working with Ajin over the past year has been a real asset to our team. They consistently deliver high-quality episode edits, trailers and short-form content, while being reliable and easy to collaborate with. They understand podcast storytelling, pacing and platform requirements very well, which makes the editing process smooth.",
  },
  {
    id: 2,
    username: "Oscar daisy",
    text: "Ajin has been an incredible editor for my Instagram content. His skills are top-notch, bringing my ideas to life with creativity and precision. Working with him has been a breeze – he's reliable, communicative, and always delivers beyond expectations. If you're looking for someone to take your videos to the next level, Ajin is the one to go to!",
  },
  {
    id: 3,
    username: "Focas",
    text: "Ajin has been our editor for all our content, his Edit quality, delivery speed, and how he understands our requirements are the best I have seen in any editor. We will be working with him further and producing more content for our brand Instagram and YouTube",
  },
];

export default function LovedByCreators() {
  const navigate = useNavigate();

  const listVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };

  // ref for the testimonial video so we can autoplay/pause when in view
  const videoRef = useRef(null);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;

    // ensure looping
    vid.loop = true;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // play when at least 50% visible (muted to allow autoplay)
            const playPromise = vid.play();
            if (playPromise && playPromise.catch) playPromise.catch(() => {});
          } else {
            // pause when not visible enough
            try {
              vid.pause();
            } catch (e) {
              // ignore
            }
          }
        });
      },
      { threshold: [0.5] }
    );

    observer.observe(vid);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      id="reviews"
        className="py-24 px-4 sm:px-6 font-inter bg-transparent pt-[50px] md:pt-[0px] pb-[20px]"
    >
  {/* HEADER */}
      <div className="text-center mb-10">
        <div className="flex justify-center mb-[10px] md:mb-[17px]">
          <div className="px-5 py-1.5 rounded-full bg-white border border-dashed border-black/10 flex items-center gap-2 shadow-sm">
            <svg
              width="10"
              height="10"
              viewBox="0 0 256 256"
              fill="#3C5DF7"
              className="animate-spin"
              style={{ animationDuration: "5s" }}
            >
              <path d="M240,128a15.79,15.79,0,0,1-10.5,15l-63.44,23.07L143,229.5a16,16,0,0,1-30,0L89.94,166.06,26.5,143a16,16,0,0,1,0-30L89.94,89.94,113,26.5a16,16,0,0,1,30,0l23.07,63.44L229.5,113A15.79,15.79,0,0,1,240,128Z" />
            </svg>
            <span className="text-[15px] font-medium text-gray-700">Testimonial</span>
          </div>
        </div>

        <h2 className="text-[56px] font-semibold leading-[0.9]">
          Loved by{" "}
          <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}>
            Creators{" "}
          </span>
          and <br />
          <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}>
            Entrepreneurs
          </span>
        </h2>
        <p className="text-center text-[15.5px] text-[#555] max-w-[420px] mt-[10px] mb-[47px] leading-[1.6] font-[600] mx-auto">
          Why creators love working with Bloom Social.
        </p>
      </div>

      {/* MAIN GRID */}
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 md:pr-[0px] md:pl-[160px]">
        {/* LEFT STACK - animated list */}
        <motion.div
          className="flex flex-col gap-[22px]"
          variants={listVariants}
          initial="hidden"
          animate="show"
        >
          {reviews.map((review) => (
            <motion.div
              key={review.id}
              variants={itemVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              whileHover={{ y: -4, scale: 1.01 }}
              className="group bg-white border border-black/10 rounded-[22px] p-6 shadow-[0_8px_20px_rgba(0,0,0,0.08)] w-full max-w-[520px] mx-auto min-h-[160px] flex flex-col justify-center text-left transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
            >
              <div className="mb-2">
                <span className="text-[13px] font-semibold text-gray-700">
                  {review.username}
                </span>
              </div>

              <p className="text-gray-800 text-[13px] leading-relaxed">
                {review.text}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* RIGHT: single animated video testimonial */}
        <div className="h-fit flex justify-center md:justify-start md:ml-[20px] md:pt-[0px] pt-[25px]">
          <div
            className="bg-white rounded-[20px] p-4 w-full max-w-[344.1px] shadow-[0_8px_20px_rgba(0,0,0,0.08)] relative border border-black/10 hover:shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
          >
            <div className="overflow-hidden rounded-[12px] bg-black relative">
              <iframe
                className="w-full h-[560px] sm:h-[620px] md:h-[551px] h-[551px] object-cover"
                src="https://www.youtube.com/embed/UkfbG9COjkY?autoplay=1&mute=1&loop=1&playlist=UkfbG9COjkY&controls=1&modestbranding=1&rel=0"
                title="YouTube Shorts testimonial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>

            {/* Removed bottom reviewer block to match vertical Shorts layout */}
      </div>
        </div>
      </div>

      {/* BOOK A CALL - moved to bottom of section */}
      <div className="mt-12 flex justify-center">
        <button
          onClick={() => navigate("/contact")}
          className="bg-black text-white px-8 py-3 rounded-full font-medium text-[16px] flex items-center gap-2"
        >
          Book a Call
          <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
            <svg width="14" height="14" stroke="white" viewBox="0 0 24 24">
              <path d="M5 12H19M12 5l7 7-7 7" strokeWidth="2" />
            </svg>
          </div>
        </button>
      </div>
    </section>
  );
}
